import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeck } from '../lib/deckContext';
import { type RenderKind } from '../lib/deck';
import {
  fetchSavedDeck,
  updateSavedDeck,
  exportPdf,
  fetchLayouts,
  type SavedDeckDetail,
  type RenderUrls,
} from '../lib/api';
import { DeckImageGrid } from '../components/DeckImageGrid';

export function MyDeckDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { deck: workingDeck, loadSavedDeck } = useDeck();

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; deck: SavedDeckDetail }
    | { status: 'error'; message: string }
  >({ status: 'loading' });
  const [rendersByCode, setRendersByCode] = useState<Record<string, Record<string, RenderUrls>>>({});
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    fetchSavedDeck(id)
      .then((r) => {
        if (cancelled) return;
        setState({ status: 'ready', deck: r.deck });
        setName(r.deck.name);
        setNotes(r.deck.notes ?? '');
      })
      .catch((e) => !cancelled && setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }));
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetchLayouts()
      .then((r) => {
        if (cancelled) return;
        const map: Record<string, Record<string, RenderUrls>> = {};
        for (const l of r.layouts) map[l.code] = l.renders;
        setRendersByCode(map);
      })
      .catch(() => {/* tiles fall back to placeholders */});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!id) return;
    setSaveState('saving');
    try {
      await updateSavedDeck(id, { name: name.trim() || 'Untitled deck', notes });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1800);
    } catch {
      setSaveState('error');
    }
  };

  const handleRedownload = async (deck: SavedDeckDetail): Promise<void> => {
    setExporting(true);
    try {
      await exportPdf({
        mode: 'deck',
        items: deck.items,
        customerName: deck.customerName || undefined,
        deckName: deck.name || undefined,
      });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not download the PDF.');
    } finally {
      setExporting(false);
    }
  };

  const handleLoadEdit = (deck: SavedDeckDetail): void => {
    if (
      workingDeck.items.length > 0 &&
      !window.confirm('Replace your current working deck with this saved deck?')
    ) {
      return;
    }
    loadSavedDeck(
      deck.items.map((i) => ({ ...i, selectedRenders: i.selectedRenders as RenderKind[], addedAt: Date.now() })),
      deck.customerName,
      deck.name,
    );
    navigate('/deck');
  };

  return (
    <div className="h-full overflow-y-auto bg-vari-beige/20">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Link to="/my-decks" className="text-sm text-vari-blue-accessible hover:underline">
          ← All decks
        </Link>

        {state.status === 'loading' && <p className="text-vari-gray text-sm">Loading…</p>}

        {state.status === 'error' && (
          <div className="rounded-lg border border-vari-red/30 bg-vari-red/5 px-5 py-4 text-sm text-vari-navy">
            {state.message}
          </div>
        )}

        {state.status === 'ready' && (
          <>
            {/* Name + internal notes */}
            <div className="rounded-2xl border border-vari-soft-gray bg-white p-5 sm:p-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
                  Deck name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-vari-soft-gray px-3 py-2 text-lg font-semibold text-vari-navy focus:border-vari-navy focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
                  Internal notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Notes for your own reference — not shown to customers."
                  className="mt-1 w-full rounded-lg border border-vari-soft-gray px-3 py-2 text-sm text-vari-navy focus:border-vari-navy focus:outline-none resize-y"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saveState === 'saving'}
                  className="rounded-lg bg-vari-navy text-white px-4 py-2 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors disabled:opacity-50"
                >
                  {saveState === 'saving' ? 'Saving…' : 'Save'}
                </button>
                {saveState === 'saved' && <span className="text-xs text-vari-blue-accessible">✓ Saved</span>}
                {saveState === 'error' && <span className="text-xs text-vari-red">Couldn't save</span>}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleRedownload(state.deck)}
                disabled={exporting}
                className="rounded-lg bg-vari-navy text-white px-4 py-2 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors disabled:opacity-50"
              >
                {exporting ? 'Preparing…' : 'Re-download PDF'}
              </button>
              <button
                type="button"
                onClick={() => handleLoadEdit(state.deck)}
                className="rounded-lg border border-vari-navy text-vari-navy px-4 py-2 text-sm font-semibold hover:bg-vari-navy hover:text-white transition-colors"
              >
                Load &amp; edit
              </button>
            </div>

            {/* Images */}
            <DeckImageGrid items={state.deck.items} rendersByCode={rendersByCode} />
          </>
        )}
      </div>
    </div>
  );
}
